import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import type {
  DocumentDetailResponse,
  DocumentResponse,
  EffectivePermissionsResponse,
  FolderCreatedResponse,
  FolderResponse,
  CreateInvitationRequest,
  GroupResponse,
  InvitationCreatedResponse,
  InvitationResponse,
  NdaRecordResponse,
  DataPolicyResponse,
  NodeType,
  OrganizationResponse,
  PermissionRowResponse,
  PrincipalType,
  RoomMemberResponse,
  RoomResponse,
  RoomStatus,
  RoomSummary,
  SetPermissionRequest,
  UploadResponse,
  UserSummary,
  ZipUploadResponse,
  QuestionResponse,
  AskQuestionRequest,
  SearchResponse,
  ActivityReportResponse,
  DocumentActivityResponse,
  GroupActivityResponse,
  TableReportResponse,
  UserActivityResponse,
  NotificationPreferences,
  ArchiveResponse,
  ChecklistBulkBody,
  ChecklistHistoryEntry,
  ChecklistItemBody,
  ChecklistPrincipal,
  ChecklistItemResponse,
  ChecklistItemStatus,
  ChecklistResponse,
  ChecklistSectionBody,
  ChecklistSectionResponse,
  ChecklistTagResponse,
  InstantiateChecklistResponse,
} from './types'

/*
  Query keys are hierarchical so an invalidation can be as narrow as one folder's
  documents or as wide as everything about a room.
*/
export const keys = {
  rooms: ['rooms'] as const,
  room: (roomId: string) => ['rooms', roomId] as const,
  folders: (roomId: string) => ['rooms', roomId, 'folders'] as const,
  documents: (roomId: string, folderId: string | null) =>
    ['rooms', roomId, 'documents', folderId ?? 'root'] as const,
  document: (roomId: string, documentId: string) =>
    ['rooms', roomId, 'document', documentId] as const,
  members: (roomId: string) => ['rooms', roomId, 'members'] as const,
  groups: (roomId: string) => ['rooms', roomId, 'groups'] as const,
  permissions: (roomId: string, nodeType: NodeType, nodeId: string) =>
    ['rooms', roomId, 'permissions', nodeType, nodeId] as const,
  effective: (roomId: string, nodeType: NodeType, nodeId: string) =>
    ['rooms', roomId, 'effective', nodeType, nodeId] as const,
  ndaRecord: (roomId: string) => ['rooms', roomId, 'nda-record'] as const,
  dataPolicy: (organizationId: string) => ['organizations', organizationId, 'data-policy'] as const,
  invitations: (roomId: string) => ['rooms', roomId, 'invitations'] as const,
  questions: (roomId: string) => ['rooms', roomId, 'questions'] as const,
  archives: (roomId: string) => ['rooms', roomId, 'archives'] as const,
  checklist: (roomId: string) => ['rooms', roomId, 'checklist'] as const,
  documentsByItem: (roomId: string, itemId: string) =>
    ['rooms', roomId, 'documents-by-item', itemId] as const,
  search: (roomId: string, q: string) => ['rooms', roomId, 'search', q] as const,
  notifications: (roomId: string) => ['rooms', roomId, 'notifications'] as const,
  report: (roomId: string, kind: string, params: unknown) =>
    ['rooms', roomId, 'report', kind, params] as const,
  question: (roomId: string, questionId: string) =>
    ['rooms', roomId, 'question', questionId] as const,
  organizations: ['organizations'] as const,
}

const data = <T>(p: Promise<{ data: T }>) => p.then((r) => r.data)

// ------------------------------------------------------------------ reads

export const useRooms = () =>
  useQuery({ queryKey: keys.rooms, queryFn: () => data(api.get<RoomSummary[]>('/api/rooms')) })

export const useRoom = (roomId: string) =>
  useQuery({
    queryKey: keys.room(roomId),
    queryFn: () => data(api.get<RoomResponse>(`/api/rooms/${roomId}`)),
  })

export const useFolders = (roomId: string) =>
  useQuery({
    queryKey: keys.folders(roomId),
    queryFn: () => data(api.get<FolderResponse[]>(`/api/rooms/${roomId}/folders`)),
  })

export const useDocuments = (roomId: string, folderId: string | null) =>
  useQuery({
    queryKey: keys.documents(roomId, folderId),
    queryFn: () =>
      data(
        api.get<DocumentResponse[]>(
          `/api/rooms/${roomId}/documents${folderId ? `?folderId=${folderId}` : ''}`,
        ),
      ),
  })

export const useDocument = (roomId: string, documentId: string | null) =>
  useQuery({
    queryKey: keys.document(roomId, documentId ?? ''),
    queryFn: () =>
      data(api.get<DocumentDetailResponse>(`/api/rooms/${roomId}/documents/${documentId}`)),
    enabled: documentId !== null,
  })

export const useMembers = (roomId: string, enabled = true) =>
  useQuery({
    queryKey: keys.members(roomId),
    queryFn: () => data(api.get<RoomMemberResponse[]>(`/api/rooms/${roomId}/members`)),
    enabled,
  })

export const useGroups = (roomId: string, enabled = true) =>
  useQuery({
    queryKey: keys.groups(roomId),
    queryFn: () => data(api.get<GroupResponse[]>(`/api/rooms/${roomId}/groups`)),
    enabled,
  })

export const usePermissionRows = (roomId: string, nodeType: NodeType, nodeId: string) =>
  useQuery({
    queryKey: keys.permissions(roomId, nodeType, nodeId),
    queryFn: () =>
      data(
        api.get<PermissionRowResponse[]>(
          `/api/rooms/${roomId}/permissions?nodeType=${nodeType}&nodeId=${nodeId}`,
        ),
      ),
  })

export const useEffectivePermissions = (roomId: string, nodeType: NodeType, nodeId: string) =>
  useQuery({
    queryKey: keys.effective(roomId, nodeType, nodeId),
    queryFn: () =>
      data(
        api.get<EffectivePermissionsResponse>(
          `/api/rooms/${roomId}/permissions/effective?nodeType=${nodeType}&nodeId=${nodeId}`,
        ),
      ),
  })

export const useNdaRecord = (roomId: string, enabled = true) =>
  useQuery({
    queryKey: keys.ndaRecord(roomId),
    queryFn: () => data(api.get<NdaRecordResponse>(`/api/rooms/${roomId}/nda/record`)),
    enabled,
  })

export const useInvitations = (roomId: string, enabled = true) =>
  useQuery({
    queryKey: keys.invitations(roomId),
    queryFn: () => data(api.get<InvitationResponse[]>(`/api/rooms/${roomId}/invitations`)),
    enabled,
  })

export const useOrganizations = (enabled: boolean) =>
  useQuery({
    queryKey: keys.organizations,
    queryFn: () => data(api.get<OrganizationResponse[]>('/api/organizations')),
    enabled,
  })

export const searchUsers = (organizationId: string, q: string) =>
  data(
    api.get<UserSummary[]>(`/api/organizations/${organizationId}/users?q=${encodeURIComponent(q)}`),
  )

// ------------------------------------------------------------------ writes

/** Invalidates everything about one room, which is what most writes need. */
function useRoomInvalidator(roomId: string) {
  const client = useQueryClient()
  return () => client.invalidateQueries({ queryKey: keys.room(roomId) })
}

export function useCreateOrganization() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { name: string; slug: string }) =>
      data(api.post<OrganizationResponse>('/api/organizations', body)),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.organizations })
      void client.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

export function useCreateRoom() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { organizationId: string; name: string; description: string | null }) =>
      data(api.post<RoomSummary>('/api/rooms', body)),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.rooms }),
  })
}

export function useSetRoomStatus(roomId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (status: RoomStatus) =>
      data(api.post<RoomResponse>(`/api/rooms/${roomId}/status`, { status })),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.room(roomId) })
      void client.invalidateQueries({ queryKey: keys.rooms })
    },
  })
}

/** The room comes back with the caller's new bits, so the room query is replaced, not just invalidated. */
function useRoomMutation<TVariables>(roomId: string, fn: (v: TVariables) => Promise<RoomResponse>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (room) => {
      client.setQueryData(keys.room(roomId), room)
      void client.invalidateQueries({ queryKey: keys.room(roomId), refetchType: 'none' })
      void client.invalidateQueries({ queryKey: keys.folders(roomId) })
      void client.invalidateQueries({ queryKey: ['rooms', roomId, 'documents'] })
      void client.invalidateQueries({ queryKey: keys.ndaRecord(roomId) })
    },
  })
}

export const useSetNda = (roomId: string) =>
  useRoomMutation(roomId, (body: { title: string; body: string }) =>
    data(api.put<RoomResponse>(`/api/rooms/${roomId}/nda`, body)),
  )

export const useRemoveNda = (roomId: string) =>
  useRoomMutation(roomId, () => data(api.delete<RoomResponse>(`/api/rooms/${roomId}/nda`)))

export const useAcceptNda = (roomId: string) =>
  useRoomMutation(roomId, (versionId: string) =>
    data(api.post<RoomResponse>(`/api/rooms/${roomId}/nda/accept`, { versionId })),
  )

/** The caller agrees to the organization's data policy (ADR 0008). The room comes back with `dataPolicy.accepted`. */
export const useAcceptDataPolicy = (roomId: string) =>
  useRoomMutation(roomId, (versionId: string) =>
    data(api.post<RoomResponse>(`/api/rooms/${roomId}/data-policy/accept`, { versionId })),
  )

/** The organization's policy and its acceptance record, for the organization's Owners. The read is audited. */
export const useDataPolicy = (organizationId: string) =>
  useQuery({
    queryKey: keys.dataPolicy(organizationId),
    queryFn: () =>
      data(api.get<DataPolicyResponse>(`/api/organizations/${organizationId}/data-policy`)),
    // A 403 or 404 is an answer, not an outage: say so at once rather than after a retry.
    retry: false,
  })

/** Publish (always a new version) or take down the policy. Every room's answer carries the policy's state, so rooms are refetched. */
function useDataPolicyMutation<TVariables>(
  organizationId: string,
  fn: (v: TVariables) => Promise<DataPolicyResponse>,
) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (policy) => {
      client.setQueryData(keys.dataPolicy(organizationId), policy)
      void client.invalidateQueries({ queryKey: keys.rooms })
    },
  })
}

export const useSetDataPolicy = (organizationId: string) =>
  useDataPolicyMutation(organizationId, (body: { title: string; body: string }) =>
    data(api.put<DataPolicyResponse>(`/api/organizations/${organizationId}/data-policy`, body)),
  )

export const useRemoveDataPolicy = (organizationId: string) =>
  useDataPolicyMutation(organizationId, () =>
    data(api.delete<DataPolicyResponse>(`/api/organizations/${organizationId}/data-policy`)),
  )

export function useUpdateRoom(roomId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { name: string; description: string | null; watermarkEnabled: boolean }) =>
      data(api.put<RoomResponse>(`/api/rooms/${roomId}`, body)),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.room(roomId) })
      void client.invalidateQueries({ queryKey: keys.rooms })
    },
  })
}

export function useCreateFolder(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: (body: { parentId: string | null; name: string }) =>
      data(api.post<FolderCreatedResponse>(`/api/rooms/${roomId}/folders`, body)),
    onSuccess: () => void invalidate(),
  })
}

export function useRenameFolder(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({ folderId, name }: { folderId: string; name: string }) =>
      api.put<void>(`/api/rooms/${roomId}/folders/${folderId}/name`, { name }),
    onSuccess: () => void invalidate(),
  })
}

export function useDeleteFolder(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: (folderId: string) => api.del(`/api/rooms/${roomId}/folders/${folderId}`),
    onSuccess: () => void invalidate(),
  })
}

/** The upload query string: the folder and the checklist items the file answers (ADR 0006), each repeated. */
function uploadQuery(folderId: string | null, itemIds: string[] | undefined, name?: string) {
  const params = new URLSearchParams()
  if (name) params.set('name', name)
  if (folderId) params.set('folderId', folderId)
  for (const id of itemIds ?? []) params.append('checklistItemId', id)
  const q = params.toString()
  return q ? `?${q}` : ''
}

export function useUpload(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({
      folderId,
      file,
      itemIds,
    }: {
      folderId: string | null
      file: File
      itemIds?: string[]
    }) =>
      data(
        api.upload<UploadResponse>(
          `/api/rooms/${roomId}/documents${uploadQuery(folderId, itemIds, file.name)}`,
          file,
        ),
      ),
    onSuccess: () => void invalidate(),
  })
}

export function useUploadZip(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({
      folderId,
      file,
      itemIds,
    }: {
      folderId: string | null
      file: File
      itemIds?: string[]
    }) =>
      data(
        api.upload<ZipUploadResponse>(
          `/api/rooms/${roomId}/documents/zip${uploadQuery(folderId, itemIds)}`,
          file,
          'application/zip',
        ),
      ),
    onSuccess: () => void invalidate(),
  })
}

// ------------------------------------------------------------------ checklist (ADR 0006)

export const useChecklist = (roomId: string) =>
  useQuery({
    queryKey: keys.checklist(roomId),
    queryFn: () => data(api.get<ChecklistResponse>(`/api/rooms/${roomId}/checklist`)),
  })

/** The room's documents answering one item, wherever they sit. */
export const useDocumentsByChecklistItem = (roomId: string, itemId: string, enabled = true) =>
  useQuery({
    queryKey: keys.documentsByItem(roomId, itemId),
    queryFn: () =>
      data(api.get<DocumentResponse[]>(`/api/rooms/${roomId}/documents?checklistItemId=${itemId}`)),
    enabled,
  })

export function useCreateChecklistItem(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: (body: ChecklistItemBody) =>
      data(api.post<ChecklistItemResponse>(`/api/rooms/${roomId}/checklist`, body)),
    onSuccess: () => void invalidate(),
  })
}

/**
 * An owner's edit. `includesRequestFields` is always sent true from here: without it the API
 * writes title and description only, which is how a page loaded before the request list
 * shipped is kept from erasing it (ADR 0007).
 */
export function useUpdateChecklistItem(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({ itemId, ...body }: ChecklistItemBody & { itemId: string }) =>
      data(
        api.put<ChecklistItemResponse>(`/api/rooms/${roomId}/checklist/${itemId}`, {
          ...body,
          includesRequestFields: true,
          // The same guard for the dates and pointers (ADR 0009 s15): this form shows them, so it may clear them.
          includesTrackingFields: true,
        }),
      ),
    onSuccess: () => void invalidate(),
  })
}

/** The responder's answer in words. `expectedNotesUtc` is what was read; a save over newer notes is a 409 and overwrites nothing. */
export function useSetChecklistNotes(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({
      itemId,
      responderNotes,
      expectedNotesUtc,
    }: {
      itemId: string
      responderNotes: string | null
      expectedNotesUtc: string | null
    }) =>
      data(
        api.put<ChecklistItemResponse>(`/api/rooms/${roomId}/checklist/${itemId}/notes`, {
          responderNotes,
          checkExpected: true,
          expectedNotesUtc,
        }),
      ),
    // On a conflict too: the list then shows what the other person wrote.
    onSettled: () => void invalidate(),
  })
}

/** The whole set of assignees of one item; empty clears it. */
export function useSetChecklistAssignees(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({ itemId, assignees }: { itemId: string; assignees: ChecklistPrincipal[] }) =>
      data(
        api.put<ChecklistItemResponse>(`/api/rooms/${roomId}/checklist/${itemId}/assignees`, {
          assignees,
        }),
      ),
    onSuccess: () => void invalidate(),
  })
}

export function useBulkUpdateChecklist(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: (body: ChecklistBulkBody) =>
      data(api.post<ChecklistResponse>(`/api/rooms/${roomId}/checklist/items/bulk`, body)),
    onSuccess: () => void invalidate(),
  })
}

export function useSetChecklistSectionFolder(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({ sectionId, folderId }: { sectionId: string; folderId: string | null }) =>
      data(
        api.put<ChecklistSectionResponse>(
          `/api/rooms/${roomId}/checklist/sections/${sectionId}/folder`,
          { folderId },
        ),
      ),
    onSuccess: () => void invalidate(),
  })
}

/** What has happened to one request. An audited read, so it is fetched only when someone opens it. */
export const useChecklistHistory = (roomId: string, itemId: string, enabled: boolean) =>
  useQuery({
    queryKey: ['rooms', roomId, 'checklist', itemId, 'history'] as const,
    queryFn: () =>
      data(api.get<ChecklistHistoryEntry[]>(`/api/rooms/${roomId}/checklist/${itemId}/history`)),
    enabled,
    retry: false,
  })

export function useDeleteChecklistItem(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: (itemId: string) => api.del(`/api/rooms/${roomId}/checklist/${itemId}`),
    onSuccess: () => void invalidate(),
  })
}

/**
 * One move of the six-state workflow. `expectedStatus` is the state the screen was showing,
 * so two people acting on one item find out (409) instead of one overwriting the other.
 */
export function useSetChecklistItemStatus(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({
      itemId,
      status,
      expectedStatus,
      reason,
    }: {
      itemId: string
      status: ChecklistItemStatus
      expectedStatus?: ChecklistItemStatus
      reason?: string
    }) =>
      data(
        api.put<ChecklistItemResponse>(`/api/rooms/${roomId}/checklist/${itemId}/status`, {
          status,
          expectedStatus: expectedStatus ?? null,
          reason: reason ?? null,
        }),
      ),
    // A refusal usually means the list moved on under the screen: show what is true now.
    onSettled: () => void invalidate(),
  })
}

export function useReorderChecklistItems(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: (body: { sectionId: string | null; itemIds: string[] }) =>
      data(api.post<ChecklistResponse>(`/api/rooms/${roomId}/checklist/items/reorder`, body)),
    onSuccess: () => void invalidate(),
  })
}

export function useCreateChecklistSection(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: (body: ChecklistSectionBody) =>
      data(api.post<ChecklistSectionResponse>(`/api/rooms/${roomId}/checklist/sections`, body)),
    onSuccess: () => void invalidate(),
  })
}

export function useUpdateChecklistSection(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({ sectionId, ...body }: ChecklistSectionBody & { sectionId: string }) =>
      data(
        api.put<ChecklistSectionResponse>(
          `/api/rooms/${roomId}/checklist/sections/${sectionId}`,
          body,
        ),
      ),
    onSuccess: () => void invalidate(),
  })
}

/** A section that still holds items is removed only with `reassignTo`, the section that takes them. */
export function useDeleteChecklistSection(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({ sectionId, reassignTo }: { sectionId: string; reassignTo?: string }) =>
      api.del(
        `/api/rooms/${roomId}/checklist/sections/${sectionId}${
          reassignTo ? `?reassignTo=${reassignTo}` : ''
        }`,
      ),
    onSuccess: () => void invalidate(),
  })
}

export function useReorderChecklistSections(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: (sectionIds: string[]) =>
      data(
        api.post<ChecklistResponse>(`/api/rooms/${roomId}/checklist/sections/reorder`, {
          sectionIds,
        }),
      ),
    onSuccess: () => void invalidate(),
  })
}

/** Builds the room's list from a template (the Master DRL) in one call; refused whole if it was built before. */
export function useInstantiateChecklist(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: (body: { template: string; sectionCodes: string[] | null }) =>
      data(
        api.post<InstantiateChecklistResponse>(`/api/rooms/${roomId}/checklist/instantiate`, body),
      ),
    onSuccess: () => void invalidate(),
  })
}

export function useSetDocumentChecklist(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({ documentId, itemIds }: { documentId: string; itemIds: string[] }) =>
      data(
        api.put<ChecklistTagResponse[]>(`/api/rooms/${roomId}/documents/${documentId}/checklist`, {
          itemIds,
        }),
      ),
    onSuccess: () => void invalidate(),
  })
}

export function useRenameDocument(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({ documentId, name }: { documentId: string; name: string }) =>
      api.put<void>(`/api/rooms/${roomId}/documents/${documentId}/name`, { name }),
    onSuccess: () => void invalidate(),
  })
}

export function useDeleteDocument(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: (documentId: string) => api.del(`/api/rooms/${roomId}/documents/${documentId}`),
    onSuccess: () => void invalidate(),
  })
}

/** Downloads through the API (bearer token, audit row) and hands the bytes to the browser. */
export async function downloadDocument(
  roomId: string,
  documentId: string,
  version?: number,
): Promise<void> {
  const { blob, fileName } = await api.download(
    `/api/rooms/${roomId}/documents/${documentId}/content${version ? `?version=${version}` : ''}`,
  )
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = fileName ?? 'document'
    a.rel = 'noopener'
    document.body.append(a)
    a.click()
    a.remove()
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }
}

/**
 * Fetches the PDF rendition with the bearer token and returns an object URL for an
 * <iframe>. The caller revokes it when the viewer closes. The rendition is inline,
 * never a download, and the API decides whether it is watermarked and printable.
 */
export async function previewDocument(
  roomId: string,
  documentId: string,
  version?: number,
): Promise<{ url: string; fileName: string | null; watermarked: boolean }> {
  const { blob, fileName, headers } = await api.download(
    `/api/rooms/${roomId}/documents/${documentId}/preview${version ? `?version=${version}` : ''}`,
  )
  return {
    url: URL.createObjectURL(blob),
    fileName,
    watermarked: headers.get('X-Watermarked') === 'true',
  }
}

export function useInvite(roomId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateInvitationRequest) =>
      data(api.post<InvitationCreatedResponse>(`/api/rooms/${roomId}/invitations`, body)),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.invitations(roomId) })
      void client.invalidateQueries({ queryKey: keys.members(roomId) })
      void client.invalidateQueries({ queryKey: keys.groups(roomId) })
    },
  })
}

export function useResendInvitation(roomId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) =>
      data(
        api.post<InvitationCreatedResponse>(
          `/api/rooms/${roomId}/invitations/${invitationId}/resend`,
        ),
      ),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.invitations(roomId) }),
  })
}

export function useRevokeInvitation(roomId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) =>
      api.del(`/api/rooms/${roomId}/invitations/${invitationId}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.invitations(roomId) }),
  })
}

export function useMoveDocument(roomId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ documentId, folderId }: { documentId: string; folderId: string | null }) =>
      api.put<void>(`/api/rooms/${roomId}/documents/${documentId}/location`, { folderId }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['rooms', roomId, 'documents'] })
      void client.invalidateQueries({ queryKey: ['rooms', roomId, 'document'] })
      void client.invalidateQueries({ queryKey: keys.folders(roomId) })
    },
  })
}

export function useMoveFolder(roomId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ folderId, parentId }: { folderId: string; parentId: string | null }) =>
      api.put<void>(`/api/rooms/${roomId}/folders/${folderId}/location`, {
        parentId,
        position: null,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.folders(roomId) })
      void client.invalidateQueries({ queryKey: ['rooms', roomId, 'documents'] })
    },
  })
}

export function useAddMember(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: (body: {
      userId: string
      memberRole: 'Owner' | 'Participant'
      accessEndsUtc: string | null
    }) => api.post<void>(`/api/rooms/${roomId}/members`, body),
    onSuccess: () => void invalidate(),
  })
}

export function useRemoveMember(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: (userId: string) => api.del(`/api/rooms/${roomId}/members/${userId}`),
    onSuccess: () => void invalidate(),
  })
}

/** ADR 0011: let a participant into this room without two-step verification, or require it again. */
export function useSetMfaExemption(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({ userId, exempt }: { userId: string; exempt: boolean }) =>
      api.put<void>(`/api/rooms/${roomId}/members/${userId}/mfa-exemption`, { exempt }),
    onSuccess: () => void invalidate(),
  })
}

export function useCreateGroup(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: (name: string) =>
      data(api.post<GroupResponse>(`/api/rooms/${roomId}/groups`, { name })),
    onSuccess: () => void invalidate(),
  })
}

export function useDeleteGroup(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: (groupId: string) => api.del(`/api/rooms/${roomId}/groups/${groupId}`),
    onSuccess: () => void invalidate(),
  })
}

export function useAddGroupMember(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      api.post<void>(`/api/rooms/${roomId}/groups/${groupId}/members`, {
        userId,
        accessEndsUtc: null,
      }),
    onSuccess: () => void invalidate(),
  })
}

export function useRemoveGroupMember(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      api.del(`/api/rooms/${roomId}/groups/${groupId}/members/${userId}`),
    onSuccess: () => void invalidate(),
  })
}

export function useSetPermission(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({
      nodeType,
      nodeId,
      body,
    }: {
      nodeType: NodeType
      nodeId: string
      body: SetPermissionRequest
    }) =>
      api.put<void>(`/api/rooms/${roomId}/permissions?nodeType=${nodeType}&nodeId=${nodeId}`, body),
    onSuccess: () => void invalidate(),
  })
}

export function useRemovePermission(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({
      nodeType,
      nodeId,
      principalType,
      principalId,
    }: {
      nodeType: NodeType
      nodeId: string
      principalType: PrincipalType
      principalId: string
    }) =>
      api.del(
        `/api/rooms/${roomId}/permissions?nodeType=${nodeType}&nodeId=${nodeId}&principalType=${principalType}&principalId=${principalId}`,
      ),
    onSuccess: () => void invalidate(),
  })
}

// ------------------------------------------------------------------ Q&A

export const useQuestions = (roomId: string, status?: string) =>
  useQuery({
    queryKey: [...keys.questions(roomId), status ?? 'all'],
    queryFn: () =>
      data(
        api.get<QuestionResponse[]>(
          `/api/rooms/${roomId}/questions${status ? `?status=${encodeURIComponent(status)}` : ''}`,
        ),
      ),
  })

export const useQuestion = (roomId: string, questionId: string | null) =>
  useQuery({
    queryKey: keys.question(roomId, questionId ?? ''),
    queryFn: () => data(api.get<QuestionResponse>(`/api/rooms/${roomId}/questions/${questionId}`)),
    enabled: questionId !== null,
  })

/** Every Q&A write answers with the question as the caller now sees it; the list and the question are refreshed. */
function useQuestionMutation<TVars>(
  roomId: string,
  fn: (vars: TVars) => Promise<QuestionResponse>,
) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (question) => {
      client.setQueryData(keys.question(roomId, question.id), question)
      void client.invalidateQueries({ queryKey: keys.questions(roomId) })
    },
  })
}

export const useAskQuestion = (roomId: string) =>
  useQuestionMutation(roomId, (body: AskQuestionRequest) =>
    data(api.post<QuestionResponse>(`/api/rooms/${roomId}/questions`, body)),
  )

export const useAnswerQuestion = (roomId: string) =>
  useQuestionMutation(roomId, (vars: { questionId: string; body: string }) =>
    data(
      api.post<QuestionResponse>(`/api/rooms/${roomId}/questions/${vars.questionId}/answers`, {
        body: vars.body,
      }),
    ),
  )

export const useAssignQuestion = (roomId: string) =>
  useQuestionMutation(roomId, (vars: { questionId: string; assigneeId: string | null }) =>
    data(
      api.put<QuestionResponse>(`/api/rooms/${roomId}/questions/${vars.questionId}/assignee`, {
        assigneeId: vars.assigneeId,
      }),
    ),
  )

export const usePublishQuestion = (roomId: string) =>
  useQuestionMutation(roomId, (vars: { questionId: string; toAll: boolean; groupIds: string[] }) =>
    data(
      api.post<QuestionResponse>(`/api/rooms/${roomId}/questions/${vars.questionId}/publication`, {
        toAll: vars.toAll,
        groupIds: vars.groupIds,
      }),
    ),
  )

export const useSetQuestionStatus = (roomId: string) =>
  useQuestionMutation(roomId, (vars: { questionId: string; status: 'Open' | 'Closed' }) =>
    data(
      api.put<QuestionResponse>(`/api/rooms/${roomId}/questions/${vars.questionId}/status`, {
        status: vars.status,
      }),
    ),
  )

/** The Q&A log as CSV, through the API (bearer token, audit row). */
/** The room's index as a file (CSV or PDF), through the API (bearer token, audit row). */
export async function exportIndex(roomId: string, format: 'csv' | 'pdf'): Promise<void> {
  const { blob, fileName } = await api.download(`/api/rooms/${roomId}/index?format=${format}`)
  saveBlob(blob, fileName ?? `index.${format}`)
}

export async function exportQuestions(roomId: string): Promise<void> {
  const { blob, fileName } = await api.download(`/api/rooms/${roomId}/questions/export.csv`)
  saveBlob(blob, fileName ?? 'questions.csv')
}

/** Hands a Blob to the browser as a download and releases the object URL afterwards. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    a.rel = 'noopener'
    document.body.append(a)
    a.click()
    a.remove()
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }
}

// ------------------------------------------------------------------ search

/** Permission-filtered search across the room; the server decides what matches and what the caller may see. */
export const useSearch = (roomId: string, q: string) =>
  useQuery({
    queryKey: keys.search(roomId, q),
    queryFn: () =>
      data(api.get<SearchResponse>(`/api/rooms/${roomId}/search?q=${encodeURIComponent(q)}`)),
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
  })

// ------------------------------------------------------------------ reports

type ReportWindowParams = { from: string | null; to: string | null }

function windowQuery(
  w: ReportWindowParams,
  extra: Record<string, string | number | null> = {},
): string {
  const q = new URLSearchParams()
  if (w.from) q.set('from', w.from)
  if (w.to) q.set('to', w.to)
  for (const [k, v] of Object.entries(extra)) {
    if (v !== null && v !== '') q.set(k, String(v))
  }
  const text = q.toString()
  return text ? `?${text}` : ''
}

export const useUserReport = (roomId: string, w: ReportWindowParams) =>
  useQuery({
    queryKey: keys.report(roomId, 'users', w),
    queryFn: () =>
      data(
        api.get<TableReportResponse<UserActivityResponse>>(
          `/api/rooms/${roomId}/reports/users${windowQuery(w)}`,
        ),
      ),
  })

export const useDocumentReport = (roomId: string, w: ReportWindowParams) =>
  useQuery({
    queryKey: keys.report(roomId, 'documents', w),
    queryFn: () =>
      data(
        api.get<TableReportResponse<DocumentActivityResponse>>(
          `/api/rooms/${roomId}/reports/documents${windowQuery(w)}`,
        ),
      ),
  })

export const useGroupReport = (roomId: string, w: ReportWindowParams) =>
  useQuery({
    queryKey: keys.report(roomId, 'groups', w),
    queryFn: () =>
      data(
        api.get<TableReportResponse<GroupActivityResponse>>(
          `/api/rooms/${roomId}/reports/groups${windowQuery(w)}`,
        ),
      ),
  })

export const useActivityReport = (
  roomId: string,
  p: ReportWindowParams & { action: string | null; skip: number; take: number },
) =>
  useQuery({
    queryKey: keys.report(roomId, 'activity', p),
    queryFn: () =>
      data(
        api.get<ActivityReportResponse>(
          `/api/rooms/${roomId}/reports/activity${windowQuery(p, { action: p.action, skip: p.skip, take: p.take })}`,
        ),
      ),
  })

/** A report as a file, through the API (bearer token, audit row). `workbook` is every report in one .xlsx. */
export async function exportReport(
  roomId: string,
  kind: 'users' | 'documents' | 'groups' | 'activity' | 'workbook',
  format: 'csv' | 'xlsx',
  w: ReportWindowParams,
): Promise<void> {
  const path =
    kind === 'workbook'
      ? `/api/rooms/${roomId}/reports/workbook.xlsx${windowQuery(w)}`
      : `/api/rooms/${roomId}/reports/${kind}${windowQuery(w, { format })}`
  const { blob, fileName } = await api.download(path)
  saveBlob(blob, fileName ?? `${kind}.${format}`)
}

// ------------------------------------------------------------------ notifications

export const useNotificationPreferences = (roomId: string) =>
  useQuery({
    queryKey: keys.notifications(roomId),
    queryFn: () => data(api.get<NotificationPreferences>(`/api/rooms/${roomId}/notifications`)),
  })

export function useSetNotificationPreferences(roomId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      digestEnabled: boolean
      qaEnabled: boolean
      expiryEnabled: boolean
      checklistEnabled?: boolean
    }) => data(api.put<NotificationPreferences>(`/api/rooms/${roomId}/notifications`, body)),
    onSuccess: (prefs) => client.setQueryData(keys.notifications(roomId), prefs),
  })
}

// ------------------------------------------------------------------ close-out packages

/** The room's packages, polled while one is being built so the owner sees it turn Ready. */
export const useArchives = (roomId: string) =>
  useQuery({
    queryKey: keys.archives(roomId),
    queryFn: () => data(api.get<ArchiveResponse[]>(`/api/rooms/${roomId}/archives`)),
    refetchInterval: (query) =>
      query.state.data?.some((a) => a.status === 'Requested' || a.status === 'Building')
        ? 5000
        : false,
  })

export function useRequestArchive(roomId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => data(api.post<ArchiveResponse>(`/api/rooms/${roomId}/archives`, {})),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.archives(roomId) }),
  })
}

/** The zip through the API (bearer token, audit row); the hash is in the X-Content-Sha256 header. */
export async function downloadArchive(roomId: string, archiveId: string): Promise<void> {
  const { blob, fileName } = await api.download(
    `/api/rooms/${roomId}/archives/${archiveId}/content`,
  )
  saveBlob(blob, fileName ?? 'closeout.zip')
}
